/**
 * PersonalUseDeclaration — ADR-025 §6.4/§6.5 mandatory declaration step.
 *
 * INTERIM WORDING derived from ADR-025 §6.1; Australian counsel review is
 * pending (§8 item 12). A counsel rewrite ships as a declaration-version bump,
 * which re-gates every member exactly once.
 *
 * Fail-closed: the Continue POST must return 200 with the new state, or the
 * user stays on this step with an inline error. A stale declaration version
 * (409) refreshes the gate state instead of guessing.
 */

import React, { useState } from 'react';

const CHOICES = [
  {
    value: 'personal',
    label: 'My own property decisions',
    hint: 'Buying, holding, or selling for yourself or your household.',
  },
  {
    value: 'professional_or_client',
    label: 'Client or professional work',
    hint: 'A person will review this. Heyward does not have a professional product yet.',
  },
];

const cardStyle = {
  background: '#ffffff',
  border: '1px solid #e5e5e5',
  borderRadius: 14,
  padding: '28px 28px 24px',
};

const PersonalUseDeclaration = ({ declarationVersion, getAuthToken, onStateChange }) => {
  const [choice, setChoice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!choice || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const baseURL = import.meta.env.REACT_APP_API_URL || window.location.origin;
      const token = getAuthToken ? await getAuthToken() : null;
      const res = await fetch(`${baseURL}/api/v1/eligibility/declaration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          declaration_version: declarationVersion,
          declared_use: choice,
        }),
      });
      if (res.status === 409) {
        // Version moved under us — refresh the gate state, never guess.
        onStateChange(null);
        return;
      }
      if (!res.ok) throw new Error(`declaration returned ${res.status}`);
      const data = await res.json();
      onStateChange(data);
    } catch (err) {
      console.error('Declaration failed:', err);
      setError("Couldn't record your declaration. Nothing was saved — try again.");
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1
        style={{
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontWeight: 400,
          fontSize: 'clamp(28px, 3.4vw, 36px)',
          lineHeight: 1.08,
          letterSpacing: '-0.02em',
          color: '#171717',
          textAlign: 'center',
          margin: '0 0 10px',
        }}
      >
        One thing to confirm.
      </h1>
      <p
        style={{
          textAlign: 'center', color: '#525252', fontSize: 14.5,
          lineHeight: 1.6, margin: '0 auto 24px', maxWidth: '48ch',
        }}
      >
        Heyward is a consumer product, built for a person making decisions
        about property they own, are considering buying, or are considering
        selling for themselves. It is not for client work, bulk extraction, or
        building a competing product. Working in property is fine. What
        matters is what you use Heyward for.
      </p>

      <div style={cardStyle}>
        <div
          style={{
            fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
            fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: '#737373', marginBottom: 14,
          }}
        >
          I&apos;ll be using Heyward for
        </div>

        <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {CHOICES.map((c) => {
            const selected = choice === c.value;
            return (
              <label
                key={c.value}
                style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                  padding: '13px 14px', borderRadius: 10, cursor: 'pointer',
                  border: `1px solid ${selected ? '#292524' : '#e5e5e5'}`,
                  background: selected ? '#fafaf9' : '#ffffff',
                  transition: 'border-color 120ms ease, background 120ms ease',
                }}
              >
                <input
                  type="radio"
                  name="declared_use"
                  value={c.value}
                  checked={selected}
                  onChange={() => setChoice(c.value)}
                  style={{ marginTop: 3, accentColor: '#292524' }}
                />
                <span>
                  <span style={{ display: 'block', fontSize: 14.5, fontWeight: 500, color: '#171717' }}>
                    {c.label}
                  </span>
                  <span style={{ display: 'block', fontSize: 13, color: '#737373', marginTop: 2, lineHeight: 1.5 }}>
                    {c.hint}
                  </span>
                </span>
              </label>
            );
          })}
        </div>

        {error && (
          <p style={{ color: '#e11d48', fontSize: 13, margin: '14px 0 0', lineHeight: 1.5 }}>
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={!choice || submitting}
          style={{
            display: 'block', width: '100%', marginTop: 18,
            padding: '12px 16px', borderRadius: 10, border: 'none',
            fontFamily: '"Inter", system-ui, sans-serif',
            fontWeight: 500, fontSize: 14.5,
            cursor: choice && !submitting ? 'pointer' : 'not-allowed',
            background: choice && !submitting ? '#292524' : '#e5e5e5',
            color: choice && !submitting ? '#ffffff' : '#a3a3a3',
            transition: 'background 150ms ease',
          }}
        >
          {submitting ? 'Recording…' : 'Continue'}
        </button>

        <p
          style={{
            margin: '12px 0 0', textAlign: 'center',
            fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
            fontSize: 10, letterSpacing: '0.1em', color: '#a3a3a3',
            textTransform: 'uppercase',
          }}
        >
          Recorded with your account · declaration v{declarationVersion || '—'}
        </p>
      </div>
    </div>
  );
};

export default PersonalUseDeclaration;
